// ─── Le righe che si toccano: Quando · Quanto dura · Dove ────────────────────
//
// 👆⭐⭐ **LA FORMA NATA ALLA TAPPA ② E CHE ORA VIVE IN DUE POSTI**: la scheda di
//    una lezione e quella di un corso. Sta qui e non dentro una delle due
//    perché la seconda che l'avesse copiata sarebbe diventata la copia che un
//    giorno non riceve la correzione fatta all'altra.
//
// ⚖️ Tre forme e non una con dei parametri: **si tocca** (apre un foglio),
//    **si va** (porta a un'altra schermata), **è ferma** (non si tocca, e dice
//    perché). Un solo componente con `apri?`/`verso?`/`ferma?` renderebbe
//    legale scriverne uno senza nessuno dei tre — cioè una riga che sembra un
//    bottone e non fa niente.

import { Link } from 'react-router-dom';

/**
 * Una riga che non si tocca.
 *
 * ⭐ `chi` dice **di chi è** quella cosa quando non è del maestro — «lo fa la
 * segreteria». Senza, una riga spenta si legge come un guasto: chi guarda prova
 * a premerla, non succede niente, e non impara niente.
 * 📌 *Una cosa che non si può fare va detta insieme a chi la fa.*
 */
export function RigaFerma({
  che,
  valore,
  chi,
}: {
  che: string;
  valore: string;
  chi?: string;
}): React.JSX.Element {
  return (
    <div className="riga-maestro ferma">
      <span className="che">{che}</span>
      <span className="valore">{valore}</span>
      {chi !== undefined && <span className="di-chi">{chi}</span>}
    </div>
  );
}

/**
 * Una riga che apre un foglio dal basso.
 *
 * ⭐ `ambito` è la cosa che il mockup ha aggiunto e che non è decorazione: su un
 * corso, «Quanto dura» cambia **tutte** le volte che restano, e leggerlo dopo
 * aver toccato sarebbe leggerlo troppo tardi.
 */
export function RigaFatta({
  che,
  valore,
  ambito,
  tagliato = false,
  era,
  apri,
}: {
  che: string;
  valore: string;
  ambito?: string;
  /**
   * ✂️ **Una riga sola, e i puntini** — serve dalla tappa ⑦a, dove il valore è
   * **la nota**: una frase scritta da una persona, cioè la cosa più lunga che
   * possa comparire qui dentro.
   *
   * ⚖️ **Non è `lungo` di `RigaVerso`, ed è l'opposto**: là il valore è il
   * **nome di un corso** e va a capo, perché un nome tagliato non si riconosce.
   * Una nota invece si **riassume**: il testo intero sta nel foglio che questa
   * riga apre, e mostrarlo tutto qui farebbe crescere la riga a un quarto di
   * schermo (📏 240 px a 320, misurati).
   * 📌 *Due valori lunghi non chiedono la stessa cura: uno va riconosciuto,
   *    l'altro va solo ricordato.*
   */
  tagliato?: boolean;
  /** ⇒ `Era`, qui sotto. */
  era?: string;
  apri: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={era === undefined ? 'riga-maestro' : 'riga-maestro cambiata'}
      onClick={apri}
    >
      <span className="che">
        {che}
        <Era era={era} />
      </span>
      {ambito !== undefined && <span className="ambito">{ambito}</span>}
      <span className={tagliato ? 'valore tagliato' : 'valore'}>{valore}</span>
      <span className="freccia" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/**
 * Una riga che porta altrove: stessa faccia, ma è un link e non un foglio.
 *
 * 👁️⭐ `lungo` distingue **un nome da una misura**, e non è un dettaglio di
 * stile: «1 ora» non si spezza mai — leggerla su due righe vuol dire leggerla
 * due volte — mentre il nome di un corso è lungo quanto qualcuno ha deciso, e
 * tenerlo su una riga sola spingeva **la freccia da sola a capo**, che è il
 * modo in cui una riga sana sembra rotta. 📏 Visto a 320 px, non rileggendo.
 * 📌 *Due valori che stanno nello stesso posto non sono la stessa cosa: uno lo
 *    ha scritto il sistema, l'altro una persona.*
 */
export function RigaVerso({
  che,
  valore,
  lungo = false,
  era,
  verso,
  stato,
}: {
  che: string;
  valore: string;
  lungo?: boolean;
  /** ⇒ `Era`, qui sotto. */
  era?: string;
  verso: string;
  /** Lo stato della navigazione: serve a chi arriva per sapere da dove. */
  stato?: unknown;
}): React.JSX.Element {
  return (
    <Link className={era === undefined ? 'riga-maestro' : 'riga-maestro cambiata'} to={verso} state={stato}>
      <span className="che">
        {che}
        <Era era={era} />
      </span>
      <span className={lungo ? 'valore lungo' : 'valore'}>{valore}</span>
      <span className="freccia" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

/**
 * ✏️ **«era …» — una riga cambiata e non ancora confermata** (voce 17).
 *
 * ⚖️ Il valore nuovo sta dove sta sempre, e il vecchio si scrive **sotto il
 * nome della riga**, piccolo: chi sta per confermare deve poter rileggere cosa
 * cambia senza ricordarselo. La riga intera prende il bordo d'attenzione
 * (`.cambiata`), così le modifiche si contano a colpo d'occhio.
 */
function Era({ era }: { era: string | undefined }): React.JSX.Element | null {
  if (era === undefined) return null;
  return <span className="era">era {era}</span>;
}
